import { useState } from 'react';
import { Button } from '@repo/ui/components/button';
import { Input } from '@repo/ui/components/form/input';
import { Court } from '@repo/shared-types';
import { Plus, X, ChevronLeft } from 'lucide-react';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@repo/ui/components/dialog';

interface TimeSlot {
  id: string;
  courtId: string;
  date: string;
  startTime: string;
  endTime: string;
}

interface CourtTimeSelectorProps {
  courts: Array<{
    court: Court;
    reservations: Array<{
      id: string;
      start: string;
      end: string;
      type: string;
      phase: string | null;
    }>;
  }>;
  tournamentStartDate: Date;
  lastDayExcluded: Date;
  selectedSlots: TimeSlot[];
  onSlotsChange: (slots: TimeSlot[]) => void;
  onAddSlot?: (slot: TimeSlot) => Promise<void>;
  onRemoveSlot?: (slotId: string) => Promise<void>;
}

export function CourtTimeSelector({
  courts,
  tournamentStartDate,
  lastDayExcluded,
  selectedSlots,
  onSlotsChange,
  onAddSlot,
  onRemoveSlot,
}: CourtTimeSelectorProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [step, setStep] = useState<'court' | 'timeBlock'>('court');
  const [selectedCourtId, setSelectedCourtId] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>('');

  const minDate = format(tournamentStartDate, 'yyyy-MM-dd');
  const maxDate = format(new Date(lastDayExcluded.getTime() - 24 * 60 * 60 * 1000), 'yyyy-MM-dd');

  // Generate 3-hour blocks that start at :00 or :30
  // From 00:00 to 20:30 (last block that fits: 20:30-23:30)
  const generateThreeHourBlocks = (): Array<{ start: string; end: string }> => {
    const blocks: Array<{ start: string; end: string }> = [];
    for (let hour = 0; hour <= 20; hour++) {
      // Block starting at :00 (e.g., 00:00-03:00, 20:00-23:00)
      blocks.push({
        start: `${String(hour).padStart(2, '0')}:00`,
        end: `${String(hour + 3).padStart(2, '0')}:00`,
      });
      // Block starting at :30 (e.g., 00:30-03:30, 20:30-23:30)
      // Only add if the end time doesn't exceed 23:30
      if (hour + 3 <= 23) {
        blocks.push({
          start: `${String(hour).padStart(2, '0')}:30`,
          end: `${String(hour + 3).padStart(2, '0')}:30`,
        });
      }
    }
    return blocks;
  };

  // Check if a time slot conflicts with existing reservations or selected slots
  const hasConflict = (
    date: string,
    startTime: string,
    endTime: string,
    courtId: string
  ): boolean => {
    if (!date || !startTime || !endTime || !courtId) return false;

    const slotStart = new Date(`${date}T${startTime}`);
    let slotEnd: Date;

    // Special handling for 23:59 - it represents the last minute of the day
    if (endTime === '23:59') {
      slotEnd = new Date(`${date}T23:59:59`);
      slotEnd.setMilliseconds(999);
    } else {
      slotEnd = new Date(`${date}T${endTime}`);
    }

    // Check against existing reservations in database
    const court = courts.find((c) => c.court.id === courtId);
    if (court) {
      const hasReservationConflict = court.reservations.some((reservation) => {
        const resStart = new Date(reservation.start);
        const resEnd = new Date(reservation.end);
        return (
          (slotStart >= resStart && slotStart < resEnd) ||
          (slotEnd > resStart && slotEnd <= resEnd) ||
          (slotStart <= resStart && slotEnd >= resEnd)
        );
      });
      if (hasReservationConflict) return true;
    }

    // Check against selected slots that haven't been saved yet
    const hasSlotConflict = selectedSlots.some((slot) => {
      if (slot.courtId !== courtId || slot.date !== date) return false;

      const existingSlotStart = new Date(`${slot.date}T${slot.startTime}`);
      const existingSlotEnd = new Date(`${slot.date}T${slot.endTime}`);
      return (
        (slotStart >= existingSlotStart && slotStart < existingSlotEnd) ||
        (slotEnd > existingSlotStart && slotEnd <= existingSlotEnd) ||
        (slotStart <= existingSlotStart && slotEnd >= existingSlotEnd)
      );
    });

    return hasSlotConflict;
  };

  // Check if a time block is available (no conflicts with reservations or selected slots)
  const isBlockAvailable = (
    block: { start: string; end: string },
    date: string,
    courtId: string
  ): boolean => {
    if (!date || !courtId) return false;
    return !hasConflict(date, block.start, block.end, courtId);
  };

  const resetModal = () => {
    setStep('court');
    setSelectedCourtId('');
    setSelectedDate('');
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    resetModal();
  };

  const handleBack = () => {
    setStep('court');
    setSelectedCourtId('');
    setSelectedDate('');
  };

  const handleSelectBlock = async (block: { start: string; end: string }) => {
    if (!selectedCourtId || !selectedDate) {
      return;
    }

    const newSlot: TimeSlot = {
      id: `${selectedCourtId}-${selectedDate}-${block.start}-${Date.now()}`,
      courtId: selectedCourtId,
      date: selectedDate,
      startTime: block.start,
      endTime: block.end,
    };

    // Validate that there are no conflicts
    if (hasConflict(newSlot.date, newSlot.startTime, newSlot.endTime, newSlot.courtId)) {
      alert(
        `El bloque ${newSlot.startTime} - ${newSlot.endTime} tiene conflicto con una reserva existente`
      );
      return;
    }

    // Add slot to local state immediately for optimistic update
    onSlotsChange([...selectedSlots, newSlot]);
    handleCloseModal();

    // Call onAddSlot for API call
    if (onAddSlot) {
      try {
        await onAddSlot(newSlot);
      } catch (error) {
        // If API call fails, remove from local state
        onSlotsChange(selectedSlots);
        throw error;
      }
    }
  };

  const removeSlot = async (slotId: string) => {
    // Remove from local state immediately for optimistic update
    const updatedSlots = selectedSlots.filter((s) => s.id !== slotId);
    onSlotsChange(updatedSlots);

    // Call onRemoveSlot if provided (for API call)
    if (onRemoveSlot) {
      try {
        await onRemoveSlot(slotId);
      } catch (error) {
        // If API call fails, restore local state
        onSlotsChange(selectedSlots);
        throw error;
      }
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 'court':
        return 'Seleccionar Cancha';
      case 'timeBlock':
        return 'Seleccionar Ventana de Tiempo';
      default:
        return 'Agregar Ventana de Tiempo';
    }
  };

  // Get selected court for display
  const selectedCourt = courts.find((c) => c.court.id === selectedCourtId);

  // Generate available blocks for the selected date and court
  const availableBlocks =
    selectedDate && selectedCourtId
      ? generateThreeHourBlocks().filter((block) =>
          isBlockAvailable(block, selectedDate, selectedCourtId)
        )
      : [];

  return (
    <div className="space-y-4">
      <Button onClick={() => setIsModalOpen(true)}>
        <Plus className="w-4 h-4" />
        Agregar Ventana de Tiempo
      </Button>

      {selectedSlots.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-medium">Ventanas Seleccionadas</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {selectedSlots.map((slot) => {
              const court = courts.find((c) => c.court.id === slot.courtId);
              return (
                <div
                  key={slot.id}
                  className="flex items-center justify-between p-2 border rounded-md bg-white"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">{court?.court.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(`${slot.date}T12:00:00`), 'dd/MM')} · {slot.startTime} -{' '}
                      {slot.endTime}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSlot(slot.id)}
                    className="p-1 hover:bg-neutral-100 rounded ml-2 shrink-0"
                  >
                    <X className="w-4 h-4 text-neutral-500" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Dialog open={isModalOpen} onOpenChange={handleCloseModal}>
        <DialogContent className="w-[90vw] max-w-[800px] max-h-[90vh] flex flex-col">
          <DialogHeader className="flex flex-row items-center justify-between gap-4 shrink-0 pb-4">
            <DialogTitle>{getStepTitle()}</DialogTitle>
            {step !== 'court' && (
              <Button variant="secondary" onClick={handleBack}>
                <ChevronLeft className="w-4 h-4" />
                Anterior
              </Button>
            )}
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-auto">
            {/* Step 1: Court Selection */}
            {step === 'court' && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {courts.map(({ court }) => (
                  <Button
                    key={court.id}
                    variant="secondary"
                    onClick={() => {
                      setSelectedCourtId(court.id);
                      setSelectedDate(minDate);
                      setStep('timeBlock');
                    }}
                    className="w-full justify-center"
                  >
                    {court.name}
                  </Button>
                ))}
              </div>
            )}

            {/* Step 2: Date and Time Block Selection */}
            {step === 'timeBlock' && (
              <div className="space-y-4">
                {selectedCourt && (
                  <div className="p-3 bg-neutral-50 border border-neutral-200 rounded-md">
                    <p className="text-sm text-muted-foreground">
                      <span className="font-medium">Cancha:</span> {selectedCourt.court.name}
                    </p>
                  </div>
                )}
                <Input
                  type="date"
                  label="Fecha"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min={minDate}
                  max={maxDate}
                />
                <p className="text-sm text-muted-foreground">
                  Selecciona un bloque de 3 horas disponible.
                </p>
                {availableBlocks.length === 0 ? (
                  <div className="p-4 bg-neutral-50 border border-neutral-200 rounded-md text-center text-sm text-muted-foreground">
                    No hay bloques de 3 horas disponibles para esta cancha y fecha.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {availableBlocks.map((block) => (
                      <Button
                        key={`${block.start}-${block.end}`}
                        variant="secondary"
                        onClick={() => handleSelectBlock(block)}
                        className="w-full justify-center"
                      >
                        {block.start} - {block.end}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
